// Tick phase 2 (IMPLEMENTATION §2.4): machines in id order, skipping ghosts and paused machines — push `out`
// to the front (a building that accepts it from that side, else the empty front tile, else wait), then the
// R2 progress loop (simple processors; Loader/Gunsmith through R4; Smelter heat through R6) or the Packer
// (R5).
//   R2  wpt by level; cost = cycleTicksL1 × workPerL1Tick. A running machine adds wpt; while prog ≥ cost and
//       < maxCompletionsPerTick: move the result to `out` (≤ outCap, else stall NO_ROOM holding prog = cost),
//       prog −= cost, then pick the next set at once (an idle machine keeps no work: prog = 0). A machine
//       blocked at prog = cost adds no work until its output moves (it is not "running").
// Busy share (R17): ticks a machine ran, per readout bucket.
import { cycleCost, isMatcher, isProcessorType, recipeOf, workPerTick } from '../bdefs.ts';
import { dropHeld, insertInto, makeRef } from '../containers.ts';
import type { Int } from '../core/types.ts';
import type { Ctx } from '../ctx.ts';
import { findBuilding, restoreItem } from '../entities.ts';
import type { BType, FamId } from '../gen/ids.ts';
import { isOpen, occAt } from '../occupancy.ts';
import { closesBucket, nextSlot, openSlot } from '../readout.ts';
import { acceptsAuto, entrySide, frontOf } from '../rules/accept.ts';
import { matcherPick } from '../rules/loaderMatch.ts';
import { packerStep } from '../rules/packer.ts';
import { clearStall, raiseStall } from '../stall.ts';
import type { Building, ItemRef, PackerB, ProcessorB } from '../state.ts';
import { freeUnreserved } from '../topology.ts';

/**
 * Pushes `out` (oldest first) to the front: a building that accepts it, else one item onto an empty tile
 * (not one a chute chain is delivering onto).
 */
export function pushOut(c: Ctx, b: Building, out: ItemRef[]): void {
  while (out.length > 0) {
    const r = out[0] as ItemRef;
    const f = frontOf(b);
    if (!isOpen(c.occ, f.x, f.y)) return;
    const id = occAt(c.occ, f.x, f.y);
    if (id === 0) {
      if (!freeUnreserved(c, f.x, f.y)) return; // a chute chain is delivering onto that tile
      out.shift();
      restoreItem(c, { id: r.id, fam: r.fam, tier: r.tier, x: f.x, y: f.y });
      c.ev.push({ e: 'spawn', id: r.id, fam: r.fam, tier: r.tier, x: f.x, y: f.y, from: b.id });
      return; // the tile is taken now
    }
    const t = findBuilding(c.s, id);
    if (t === null || !acceptsAuto(c, t, r.fam, r.tier, entrySide(t.x, t.y, b.x, b.y))) return;
    out.shift();
    insertInto(c, t, r, 0);
  }
}

/** Simple processors: take the oldest input (Smelter: when the tank holds its heat need). */
function simplePick(c: Ctx, b: ProcessorB): boolean {
  const slot = b.slots[0] as ItemRef[];
  const first = slot[0];
  if (first === undefined) {
    raiseStall(c, b, 'NO_INPUT');
    return false;
  }
  const recipe = recipeOf(c.d, b);
  if (recipe.heatNeed.length > 0) {
    const need = recipe.heatNeed[first.tier - 1] ?? 0;
    if (b.heat < need) {
      raiseStall(c, b, 'NO_HEAT');
      return false;
    }
    b.heat -= need;
  }
  slot.shift();
  dropHeld(c, first.id);
  b.proc = { fam: recipe.out, tier: first.tier };
  return true;
}

/** Records that a machine type crafted a family (the `madeBy` unlock trigger, v0.2 u34). */
function noteMadeBy(c: Ctx, type: BType, fam: FamId): void {
  const key = `${type}:${fam}`;
  if (!c.s.progress.madeBy.includes(key)) c.s.progress.madeBy.push(key);
}

function pickSet(c: Ctx, b: ProcessorB): boolean {
  return isMatcher(b.type) ? matcherPick(c, b) : simplePick(c, b);
}

/** One R2 tick of a processor. Returns true when it ran (busy). */
function processorStep(c: Ctx, b: ProcessorB): boolean {
  if (b.proc === null && !pickSet(c, b)) {
    b.prog = 0;
    return false;
  }
  const cost = cycleCost(c.d, b.type);
  let ran = false;
  if (b.prog < cost) {
    b.prog += workPerTick(c.d, b.level);
    ran = true;
  }
  let n = 0;
  while (b.proc !== null && b.prog >= cost && n < c.d.machineRules.maxCompletionsPerTick) {
    if (b.out.length >= c.d.machineRules.outCap) {
      b.prog = cost;
      raiseStall(c, b, 'NO_ROOM');
      return ran;
    }
    const r = makeRef(c, b.proc.fam, b.proc.tier);
    b.out.push(r);
    noteMadeBy(c, b.type, r.fam);
    c.ev.push({ e: 'craft', bid: b.id, id: r.id, fam: r.fam, tier: r.tier });
    n++;
    b.prog -= cost;
    b.proc = null;
    if (!pickSet(c, b)) {
      b.prog = 0;
      return ran;
    }
  }
  if (b.proc !== null) clearStall(c, b);
  return ran;
}

function markBusy(c: Ctx, busy: Int[]): void {
  const i = openSlot(c.d, c.s.tick);
  busy[i] = (busy[i] ?? 0) + 1;
}

export function runMachines(c: Ctx): void {
  for (const b of c.s.buildings) {
    if (b.ghost !== null || b.paused) continue;
    if (b.type === 'packer') {
      const p: PackerB = b;
      pushOut(c, p, p.out);
      if (packerStep(c, p)) markBusy(c, p.busy);
    } else if (isProcessorType(b.type)) {
      const p = b as ProcessorB;
      pushOut(c, p, p.out);
      if (processorStep(c, p)) markBusy(c, p.busy);
    }
  }
  if (closesBucket(c.d, c.s.tick)) {
    const next = nextSlot(c.d, c.s.tick);
    for (const b of c.s.buildings) {
      if (b.type === 'packer' || isProcessorType(b.type))
        (b as ProcessorB | PackerB).busy[next] = 0;
    }
  }
}
